/*
  parallax.js
  Subtle background parallax for any element tagged with [data-parallax].

  Usage (in Liquid):
    <div class="planta-slide__media" data-parallax data-parallax-speed="0.18">...</div>

  - speed is the fraction of scroll the element moves against the page scroll.
    0    = no movement (static)
    0.2  = subtle (recommended default for backgrounds)
    0.4  = obvious (use sparingly)
    1    = element scrolls in place (effectively fixed — not usually desired)

  Mechanism:
   - We observe each parallax element's *section* (the closest ancestor
     matching [data-parallax-section] or the element's .shopify-section).
   - Only in-view sections get updated per frame.
   - Translation is computed from how much of the section has passed the
     viewport center, multiplied by speed. Clamped so it doesn't drift far
     beyond the section's natural position.

  Performance: single shared rAF, transform-only writes.
  Accessibility: disabled under prefers-reduced-motion.
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduceMotion) return;

  /** @type {Array<{el: HTMLElement, section: HTMLElement, speed: number}>} */
  var items = [];
  var active = new Set();
  var rafId = 0;
  var viewportH = window.innerHeight;

  function getSection(el) {
    var s = el.closest('[data-parallax-section]');
    if (s) return s;
    s = el.closest('.shopify-section');
    return s || el.parentElement || el;
  }

  function scan(root) {
    var ctx = root || document;
    var els = ctx.querySelectorAll('[data-parallax]:not([data-parallax-ready])');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      el.setAttribute('data-parallax-ready', '');
      var speed = parseFloat(el.getAttribute('data-parallax-speed') || '0.2');
      if (!isFinite(speed)) speed = 0.2;
      var section = getSection(el);
      var item = { el: el, section: section, speed: speed };
      items.push(item);
      observer.observe(section);
      // Prep for smooth transforms.
      el.style.willChange = 'transform';
    }
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        // Find any item(s) whose section matches this entry.
        for (var i = 0; i < items.length; i++) {
          if (items[i].section === entry.target) {
            if (entry.isIntersecting) {
              active.add(items[i]);
            } else {
              active.delete(items[i]);
            }
          }
        }
      });
      if (active.size > 0 && !rafId) {
        rafId = requestAnimationFrame(update);
      }
    },
    { root: null, rootMargin: '50px 0px 50px 0px', threshold: 0 }
  );

  /**
   * Compute a parallax translation for a given section + speed.
   * We map scroll progress through the section to a symmetric range so the
   * layer is offset negatively as the section enters and positively as it
   * leaves (or vice versa). The effect reads as "background moves slower".
   */
  function translateFor(item) {
    var rect = item.section.getBoundingClientRect();
    var sectionH = rect.height || 1;

    // Distance of section center from viewport center, normalized to [-1..1]
    // where -1 = just entering bottom of viewport, +1 = just leaving top.
    var sectionCenter = rect.top + sectionH / 2;
    var viewportCenter = viewportH / 2;
    var travel = (sectionCenter - viewportCenter) / ((sectionH + viewportH) / 2);
    if (travel < -1) travel = -1;
    if (travel > 1) travel = 1;

    // Max offset in px — tied to section height so tall sections get more room.
    // speed * 0.5 keeps the value reasonable; for speed=0.2 you get up to 10% of sectionH.
    var maxOffset = sectionH * 0.5 * item.speed;

    // Positive travel (section leaving top): push background downward slightly
    // so it appears to move slower than the page going up. Invert sign.
    return -travel * maxOffset;
  }

  function update() {
    rafId = 0;
    if (active.size === 0) return;

    active.forEach(function (item) {
      var y = translateFor(item);
      item.el.style.transform = 'translate3d(0,' + y.toFixed(2) + 'px,0)';
    });

    rafId = requestAnimationFrame(update);
  }

  function onResize() {
    viewportH = window.innerHeight;
    if (active.size > 0 && !rafId) rafId = requestAnimationFrame(update);
  }

  function init() {
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', onResize, { passive: true });
  document.addEventListener('shopify:section:load', function (e) {
    scan(e.target);
  });
})();
